import type { AxiosInstance } from "axios";
import type { IFactoryFunctionRequest, IFactoryResponse } from "@bc-library/interface";
import { EMethod } from "@bc-library/interface";
import useAxios from "@/services/api/useAxios";
import { API_MAIN_VERSION, BASE_URL } from "@/config/constantes";
import {
  afterReceiving,
  afterReceivingMailHashTagsInfo,
  beforeSending,
} from "@/services/employeeOnboarding/hooks";
import type { ITrainingLinkProgress } from "@/services/employeeOnboarding/interface";

const resource = "employeeOnboardings";

type TEmployeeOnboardingListQuery = {
  page?: number;
  limit?: number;
  processId?: string[];
  isClosed?: "true" | "false";
  isOnboarding?: "true" | "false";
  countryId?: string[];
  managedByIds?: string[];
  sortBy?: string;
  sortOrder?: number;
  search?: string;
};

const buildEmployeeOnboardingListQuery = ({
  page,
  limit,
  processId,
  isClosed,
  isOnboarding,
  countryId,
  managedByIds,
  sortBy,
  sortOrder,
  search,
}: TEmployeeOnboardingListQuery) => {
  const query: Record<string, any> = {};

  if (page !== undefined) query.page = page;
  if (limit !== undefined) query.limit = limit;
  if (processId?.length) query.processId = processId;
  if (isClosed !== undefined) query.isClosed = isClosed;
  if (isOnboarding !== undefined) query.isOnboarding = isOnboarding;
  if (countryId?.length) query.countryId = countryId;
  if (managedByIds?.length) query.managedByIds = managedByIds;
  if (sortBy) query.sortBy = sortBy;
  if (sortOrder !== undefined) query.sortOrder = sortOrder;
  if (search) query.search = search;

  return query;
};

const createEmployeeOnboarding =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (boardingProcessId, processId, { sendInvitation = false }): IFactoryResponse<any, any> => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/create/${boardingProcessId}/${processId}`,
      query: { sendInvitation },
      beforeSendingHook: beforeSending,
    });
  };

const createHrEmployeeOnboarding =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (
    boardingProcessId,
    resourceId,
    {
      sendInvitation = false,
      phoneNumber1 = undefined,
      startDate = undefined,
      recruitmentprocessId = undefined,
      overwriteUserEmailWithPersonalEmail = false,
    },
  ): IFactoryResponse<any, any> => {
    const body = {
      sendInvitation,
      phoneNumber1,
      startDate,
      recruitmentprocessId,
      overwriteUserEmailWithPersonalEmail,
    };
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/createHr/${boardingProcessId}/${resourceId}`,
      data: body,
      beforeSendingHook: beforeSending,
    });
  };

/***
 * Return paginated employee onboarding processes
 * @param page
 * @param limit
 * @param processId
 * @returns {Promise<AxiosResponse<any>>}
 */
const getEmployeeOnboardingProcesses =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({
    page,
    limit,
    processId,
  }: {
    page?: number;
    limit?: number;
    processId?: string;
  }): IFactoryResponse<any, any> => {
    // if no processId , error
    if (!processId) throw new Error("Missing parameters");
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/process/${processId}`,
      query: { page, limit },
      afterReceivingHook: afterReceiving,
    });
  };

/**
 * Return paginated employee onboarding processes with all filters
 * GET /api/employeeOnboarding/v2/list
 */
const getEmployeeOnboardingList =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({
    page,
    limit,
    processId,
    isClosed,
    isOnboarding,
    countryId,
    managedByIds,
    sortBy,
    sortOrder,
    search,
  }: TEmployeeOnboardingListQuery): IFactoryResponse<any, any> => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/list`,
      query: buildEmployeeOnboardingListQuery({
        page,
        limit,
        processId,
        isClosed,
        isOnboarding,
        countryId,
        managedByIds,
        sortBy,
        sortOrder,
        search,
      }),
      afterReceivingHook: afterReceiving,
    });
  };

const getEmployeeOnboardingById =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (onboardingId: string): IFactoryResponse<any, any> => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/item/${onboardingId}`,
      afterReceivingHook: afterReceiving,
    });
  };

/** Progress of the trainings (path, e-learning, training) the onboarding's actions enrolled the resource in, by action id */
const getTrainingLinkProgress =
  (axios: IFactoryFunctionRequest<Record<string, ITrainingLinkProgress>, any>) =>
  (onboardingId: string): IFactoryResponse<Record<string, ITrainingLinkProgress>, any> => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/trainingLinkProgress/${onboardingId}`,
    });
  };

const exportEmployeeOnboardingListToExcel =
  (axios: IFactoryFunctionRequest<{ base64: string }, any>) =>
  ({
    processId,
    isClosed,
    isOnboarding,
    countryId,
    managedByIds,
    sortBy,
    sortOrder,
    search,
  }: TEmployeeOnboardingListQuery): IFactoryResponse<{ base64: string }, any> => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/export`,
      query: buildEmployeeOnboardingListQuery({
        processId,
        isClosed,
        isOnboarding,
        countryId,
        managedByIds,
        sortBy,
        sortOrder,
        search,
      }),
      afterReceivingHook: afterReceiving,
    });
  };

// // get employee and process info for hashtags" mail
// const retreiveMailHashTagsInfo = (axios: IFactoryFunctionRequest) => ({ processId }): IFactoryResponse<any, any> => {
//   return axios({
//     method: EMethod.get,
//     url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/boardingMailHashTags/${processId}`,
//   })
// }

const getCurrentResourceProcesses =
  (axios: IFactoryFunctionRequest<any, any>) => (): IFactoryResponse<any, any> => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/currentResource`,
      afterReceivingHook: afterReceiving,
    });
  };

const checkAction =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (processId, actionId, checked?: boolean): IFactoryResponse<any, any> => {
    return axios({
      method: EMethod.patch,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/check/${processId}/action/${actionId}`,
      data: checked === undefined ? undefined : { checked },
    });
  };

// const sendMailResponse = await employeeOnboardingApi.sendMailAction({processId: props.employeeOnboarding._id, actionId:action.value._id, subject: template.value.headline, html: template.value.content, includeSignature: includeSignature.value, attachments: attachments.value }).promise
const sendMailAction = (axios: IFactoryFunctionRequest<any, any>) => ({ processId, actionId, subject, html, includeSignature, attachments, cc, bcc, }
  : {
    processId: string;
    actionId: string;
    subject: string;
    html: string;
    includeSignature: boolean;
    attachments: any[];
    cc?: string;
    bcc?: string;
  }) => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/sendMail/${processId}/action/${actionId}`,
      data: { subject, html, includeSignature, attachments, cc, bcc },
    });
  };

const postDocument =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (processId, actionId, document): IFactoryResponse<any, any> => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/document/${processId}/${actionId}`,
      data: document,
    });
  };

const deleteDocument =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (processId, actionId): IFactoryResponse<any, any> => {
    return axios({
      method: EMethod.delete,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/document/${processId}/${actionId}`,
    });
  };

// TODO test
const archiveProcess =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (processId): IFactoryResponse<any, any> => {
    return axios({
      method: EMethod.patch,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/archive/${processId}`,
    });
  };
// set process.isDeleted = true
const deleteProcess =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (processId): IFactoryResponse<any, any> => {
    return axios({
      method: EMethod.delete,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/delete/${processId}`,
    });
  };

const retreiveMailHashTagsInfo =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({ processId, actionId }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/mailHashTags/${processId}/action/${actionId}`,
      afterReceivingHook: afterReceivingMailHashTagsInfo,
    });
  };

const getResourceProcesses =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({ resourceId }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/resource/${resourceId}`,
      afterReceivingHook: afterReceiving,
    });
  };

const retreivePopulatedField =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({ onboardingId }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/retreivePopulatedField/${onboardingId}`,
      afterReceivingHook: afterReceiving,
    });
  };

const updateActionDate =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (processId: string, actionDate: Date): IFactoryResponse<any, any> => {
    return axios({
      method: EMethod.patch,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/actionDate/${processId}`,
      data: { actionDate },
      beforeSendingHook: beforeSending,
      afterReceivingHook: afterReceiving,
    });
  };

const addManagedBy =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (processId: string, resourceId: string): IFactoryResponse<any, any> => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/managedBy/${processId}/${resourceId}`,
    });
  };

const removeManagedBy =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (processId: string, resourceId: string): IFactoryResponse<any, any> => {
    return axios({
      method: EMethod.delete,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/managedBy/${processId}/${resourceId}`,
    });
  };

const getByRecruitmentProcessId =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (recruitmentProcessId: string): IFactoryResponse<any, any> => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/recruitmentProcess/${recruitmentProcessId}`,
      afterReceivingHook: afterReceiving,
    });
  };

const regenerateSignatureAction =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (processId: string, actionId: string): IFactoryResponse<any, any> => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/regenerateSignature/${processId}/action/${actionId}`,
    });
  };

const regenerateSignatureActions =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (processId: string, actionIds: string[]): IFactoryResponse<any, any> => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/regenerateSignatures/${processId}`,
      data: { actionIds },
    });
  };

const countManagedByResource =
  (axios: IFactoryFunctionRequest<{ count: number }, any>) =>
  (resourceId: string): IFactoryResponse<{ count: number }, any> => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/countManagedBy/resource/${resourceId}`,
    });
  };

const replaceManagedByResource =
  (axios: IFactoryFunctionRequest<{ modifiedCount: number }, any>) =>
  (
    resourceId: string,
    replacementResourceIds: string[] = [],
  ): IFactoryResponse<{ modifiedCount: number }, any> => {
    return axios({
      method: EMethod.put,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/replaceManagedBy/resource/${resourceId}`,
      data: { replacementResourceIds },
    });
  };

export default (axios: AxiosInstance) => ({
  createEmployeeOnboarding: createEmployeeOnboarding(useAxios(axios)),
  createHrEmployeeOnboarding: createHrEmployeeOnboarding(useAxios(axios)),
  getEmployeeOnboardingProcesses: getEmployeeOnboardingProcesses(useAxios(axios)),
  getEmployeeOnboardingList: getEmployeeOnboardingList(useAxios(axios)),
  getEmployeeOnboardingById: getEmployeeOnboardingById(useAxios(axios)),
  getTrainingLinkProgress: getTrainingLinkProgress(useAxios(axios)),
  exportEmployeeOnboardingListToExcel: exportEmployeeOnboardingListToExcel(useAxios(axios)),
  getCurrentResourceProcesses: getCurrentResourceProcesses(useAxios(axios)),
  retreiveMailHashTagsInfo: retreiveMailHashTagsInfo(useAxios(axios)),
  checkAction: checkAction(useAxios(axios)),
  sendMailAction: sendMailAction(useAxios(axios)),
  postDocument: postDocument(useAxios(axios)),
  deleteDocument: deleteDocument(useAxios(axios)),
  archiveProcess: archiveProcess(useAxios(axios)),
  deleteProcess: deleteProcess(useAxios(axios)),
  getResourceProcesses: getResourceProcesses(useAxios(axios)),
  retreivePopulatedField: retreivePopulatedField(useAxios(axios)),
  updateActionDate: updateActionDate(useAxios(axios)),
  addManagedBy: addManagedBy(useAxios(axios)),
  removeManagedBy: removeManagedBy(useAxios(axios)),
  getByRecruitmentProcessId: getByRecruitmentProcessId(useAxios(axios)),
  regenerateSignatureAction: regenerateSignatureAction(useAxios(axios)),
  regenerateSignatureActions: regenerateSignatureActions(useAxios(axios)),
  countManagedByResource: countManagedByResource(useAxios(axios)),
  replaceManagedByResource: replaceManagedByResource(useAxios(axios)),
});
